import { Button } from '@/components';
import { PageSize, PageTypeEnum, ROUTERS, TextMessage } from '@/constant';
import { addIdSuffixForDuplicates } from '@/hooks';
import { CommonActions, DefineDocumentAreaActions } from '@/stores';
import { navigateCreateOrDetail, showAlert, toCamelCase } from '@/utils';
import { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
	DefineDocumentDownloadButtonProps,
	TDefineDocumentDownloadItem,
} from './type';

const DefineDocumentDownloadButton = (
	props: DefineDocumentDownloadButtonProps
) => {
	const dispatch = useDispatch();
	const [items, setItems] = useState<TDefineDocumentDownloadItem[]>(
		props.items || []
	);

	useEffect(() => {
		setItems(props.items || []);
	}, [props.items]);

	const getFieldAndTable = useCallback(() => {
		switch (props.pageType) {
			case PageTypeEnum.UNAUTHORIZED_DEPARTURE:
			case PageTypeEnum.CHANGE_WORK_PLACE:
				return {
					field: 'FULL_NAME',
					table: PageTypeEnum.APPLICANT,
				};
			case PageTypeEnum.FRANCHISE:
				return {
					field: 'EMPLOYER_NAME',
					table: PageTypeEnum.FRANCHISE,
				};
			case PageTypeEnum.LODGING:
				return {
					field: 'ADDRESS',
					table: PageTypeEnum.LODGING,
				};
			case PageTypeEnum.PROGRAM:
				return {
					field: 'NAME',
					table: PageTypeEnum.PROGRAM,
				};

			case PageTypeEnum.RECOMMENDER:
				return {
					field: 'NAME',
					table: PageTypeEnum.RECOMMENDER,
				};

			default:
				return {
					field: '',
					table: '',
				};
		}
	}, [props.pageType]);

	const handleCheckToNavigate = (
		propsItems: TDefineDocumentDownloadItem[] = items
	) => {
		dispatch(
			DefineDocumentAreaActions.getDefineDocumentAreaListRequest({
				pageSize: PageSize.S,
				page: 1,
				refTableName: props.pageType || '',
				refTableId:
					propsItems
						?.map((item: TDefineDocumentDownloadItem) => item.id)
						?.join(',') || '',
				callback: (params) => {
					if (params?.data === 0) {
						return showAlert(TextMessage.COMMON_NO_DOCUMENTS);
					}

					const popup = navigateCreateOrDetail(ROUTERS.DD1_01);

					const getDataFromPopup = (event: MessageEvent) => {
						if (
							event.origin !== window.location.origin ||
							event.data !== 'ready'
						) {
							if (event.data?.fromScreen === 'DOWNLOAD_DOCUMENT') {
								// TODO: when close page popup
							}
							return;
						}

						if (popup && !popup.closed) {
							const dataSend = {
								fromScreen: 'DOWNLOAD_DOCUMENT',
								items: propsItems,
								pageType: props.pageType,
								headerText: props.headerText,
								showIssuanceApplication: props.showIssuanceApplication,
							};

							popup.postMessage(dataSend, window.location.origin);
						}
					};

					window?.addEventListener('message', getDataFromPopup);
				},
			})
		);
	};

	const handleSetItemsName = useCallback(() => {
		if (!items?.length) {
			return showAlert(TextMessage.ALERT_3);
		}

		// check if items don't have name
		const itemsWithoutName = items?.filter((item) => !item.name);
		if (itemsWithoutName.length) {
			const { field, table } = getFieldAndTable();

			// call api to get name of items
			dispatch(
				CommonActions.getFieldsRequest({
					ids: itemsWithoutName.map((item) => item?.subId || item.id),
					table: table,
					fields: [field],
					callback: (response) => {
						const convertField = toCamelCase(field);
						if (!items) return;

						const updatedItems = items?.map((item) => {
							const foundItem = response?.data?.find(
								(resItem: any) =>
									(item?.subId && resItem.id === item?.subId) ||
									(!item?.subId && resItem.id === item.id)
							);
							return {
								...item,
								name:
									foundItem &&
									foundItem.fields &&
									convertField in foundItem.fields &&
									typeof foundItem.fields === 'object'
										? (foundItem.fields as Record<string, string>)[convertField]
										: item.name,
							};
						});

						const itemsWithSuffix = addIdSuffixForDuplicates(updatedItems);
						// add id suffix for duplicates
						setItems(itemsWithSuffix || []);

						// check to navigate
						handleCheckToNavigate(itemsWithSuffix);
					},
				})
			);
		} else {
			// check to navigate
			handleCheckToNavigate();
		}
	}, [items, getFieldAndTable, dispatch, handleCheckToNavigate]);

	return (
		<div>
			<Button onClick={handleSetItemsName}>서류 다운로드</Button>
		</div>
	);
};

export default memo(DefineDocumentDownloadButton);
